/**
 * UI 상태 관리 스토어
 * - 사이드바 상태
 * - 모바일 감지
 * - 테마 설정
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 사이드바 상태 타입
 */
export type SidebarState = 'expanded' | 'collapsed' | 'mobile';

/**
 * UI 상태 타입
 */
interface UIState {
  // 사이드바 상태
  sidebarState: SidebarState;
  isSidebarOpen: boolean;
  isMobile: boolean;

  // 테마 설정
  theme: 'light' | 'dark' | 'system';

  // 모달 상태
  isModalOpen: boolean;
  modalContent: React.ReactNode | null;

  // 알림 상태
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
  }>;

  // 액션
  setSidebarState: (state: SidebarState) => void;
  toggleSidebar: () => void;
  setMobile: (isMobile: boolean) => void;
  closeSidebar: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  openModal: (content: React.ReactNode) => void;
  closeModal: () => void;
  addNotification: (notification: Omit<UIState['notifications'][0], 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

/**
 * UI 스토어
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // 초기 상태
      sidebarState: 'expanded',
      isSidebarOpen: true,
      isMobile: false,
      theme: 'light',
      isModalOpen: false,
      modalContent: null,
      notifications: [],

      // 사이드바 상태 설정
      setSidebarState: (state: SidebarState) => {
        set({
          sidebarState: state,
          isSidebarOpen: state !== 'collapsed',
        });
      },

      // 사이드바 토글
      toggleSidebar: () => {
        const { sidebarState, isMobile } = get();

        if (isMobile) {
          set({ isSidebarOpen: !get().isSidebarOpen });
        } else {
          const newState =
            sidebarState === 'expanded' ? 'collapsed' : 'expanded';
          set({
            sidebarState: newState,
            isSidebarOpen: newState === 'expanded',
          });
        }
      },

      // 모바일 상태 설정
      setMobile: (isMobile: boolean) => {
        set({ isMobile });
        if (isMobile) {
          set({ isSidebarOpen: false });
        }
      },

      // 사이드바 닫기
      closeSidebar: () => {
        set({ isSidebarOpen: false });
      },

      // 테마 설정
      setTheme: (theme: 'light' | 'dark' | 'system') => {
        set({ theme });
      },

      // 모달 관리
      openModal: (content: React.ReactNode) => {
        set({ isModalOpen: true, modalContent: content });
      },

      closeModal: () => {
        set({ isModalOpen: false, modalContent: null });
      },

      // 알림 관리
      addNotification: (notification) => {
        const id = Date.now().toString();
        set(state => ({
          notifications: [...state.notifications, { ...notification, id }]
        }));
      },

      removeNotification: (id: string) => {
        set(state => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },
    }),
    {
      name: 'ui-storage',
      partialize: state => ({
        sidebarState: state.sidebarState,
      }),
    }
  )
);
